import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  EntityCriteriaCompoundOperatorInput,
  RelatedEntityConfigInput,
  useEntitySelectionQueryableRelatedTypesQuery,
} from '../../../../types/generated-types';
import BorderedSection from '../../../features/shared/borderedSection';
import {
  cloneRelatedEntityFilter,
  defaultCompoundOperator,
  isValidSelectionCriteria,
} from '../helpers/constructors';
import { EntitySelectionBuilder } from './EntitySelectionBuilder';
import { IRelatedEntityConfig } from '../../../../common/types/shared/entitySelection';

export type RelationshipInfo = {
  relationshipName: string;
  relatedModelName: string;
  description: string;
};

interface RelatedEntityFilterBuilderProps {
  targetModel: string;
  relatedEntityFilter: RelatedEntityConfigInput;
  onSaveOrUpdateFilter: (filter: RelatedEntityConfigInput) => void;
  onClose: () => void;
  tiers: number;
}

export const RelatedEntityFilterBuilder: React.FC<
  RelatedEntityFilterBuilderProps
> = ({
  targetModel,
  relatedEntityFilter: suppliedRelatedEntityFilter,
  onSaveOrUpdateFilter,
  onClose,
  tiers = 1,
}) => {
  // Flag indicating if the current related entity filter is valid (can be saved/updated)
  const [isFilterValid, setIsFilterValid] = useState<boolean>(false);

  // Flag indicating if we are dealing with a 'new' instance or editing an existing one.
  const [isNew, setIsNew] = useState(false);

  // Holds relationship metadata from query based on incoming target model name
  const [relatedEntityData, setRelatedEntityData] = useState<
    RelationshipInfo[]
  >([]);

  // Holds relationship meta info for currently selected relationship
  const [selectedRelatedEntityType, setSelectedRelatedEntityType] =
    useState<RelationshipInfo | null>(null);

  // Holds current related entity filter instance being created/edited
  const [relatedEntityFilter, setRelatedEntityFilter] =
    useState<RelatedEntityConfigInput | null>(null);

  useEffect(() => {
    setIsNew(suppliedRelatedEntityFilter.relationshipName.trim().length === 0);
    setRelatedEntityFilter(
      cloneRelatedEntityFilter(suppliedRelatedEntityFilter),
    );
  }, [suppliedRelatedEntityFilter]);

  const defaultRelationshipName = 'Please Select ...';

  // Get related fields data from query based on incoming target model name
  const {
    data: rawRelatedEntityData,
    loading: rawRelatedEntityDataLoading,
    error: rawRelatedEntityDataError,
  } = useEntitySelectionQueryableRelatedTypesQuery({
    variables: {
      modelName: {
        single: targetModel,
      },
    },
    skip: !targetModel,
  });

  // Set related entity data based on query results based on incoming target model name
  useEffect(() => {
    const defaultItem: RelationshipInfo = {
      relationshipName: defaultRelationshipName,
      relatedModelName: 'Please Select ...',
      description: 'Please Select ...',
    };

    const newData = [
      defaultItem,
      ...(rawRelatedEntityData?.queryableRelatedTypes ?? []),
    ];

    setRelatedEntityData(newData);
  }, [rawRelatedEntityData]);

  useEffect(() => {
    const selectedRelationshipName =
      suppliedRelatedEntityFilter.relationshipName.trim();
    if (relatedEntityData && selectedRelationshipName.length > 0 && !isNew) {
      const selectedRelationshipInfo = relatedEntityData.find(
        (relationshipInfo) =>
          relationshipInfo.relationshipName === selectedRelationshipName,
      );
      setSelectedRelatedEntityType(selectedRelationshipInfo ?? null);
    }
  }, [isNew, relatedEntityData, suppliedRelatedEntityFilter.relationshipName]);

  // Set selected relationship meta info when user selects the desired relationship from list.
  const handleRelatedEntityTypeChange = useCallback(
    (event: any) => {
      const selectedRelationshipInfo = relatedEntityData.find(
        (relationshipInfo) =>
          relationshipInfo.relationshipName === event.target.value,
      );
      setSelectedRelatedEntityType(selectedRelationshipInfo ?? null);
    },
    [relatedEntityData],
  );

  // (Re)initialize the related entity filter based on updated selected entity relationship meta info.
  useEffect(() => {
    if (selectedRelatedEntityType?.relatedModelName) {
      const x: IRelatedEntityConfig = {
        relatedEntities: [],
        relationshipName: selectedRelatedEntityType?.relationshipName,
        selectionCriteria: defaultCompoundOperator(),
      };
      setRelatedEntityFilter(x);
    }
  }, [selectedRelatedEntityType]);

  const onUpdateRequest = useCallback(
    (
      updatedValue: EntityCriteriaCompoundOperatorInput,
      updatedRelatedEntityFilters: RelatedEntityConfigInput[],
    ) => {
      const relationshipName =
        relatedEntityFilter?.relationshipName ??
        selectedRelatedEntityType?.relationshipName ??
        null;
      if (relationshipName) {
        setRelatedEntityFilter({
          relationshipName,
          relatedEntities: updatedRelatedEntityFilters,
          selectionCriteria: updatedValue,
        });
      }
    },
    [relatedEntityFilter, selectedRelatedEntityType],
  );

  useEffect(() => {
    setIsFilterValid(
      !!relatedEntityFilter &&
        relatedEntityFilter.relationshipName?.trim()?.length > 0 &&
        relatedEntityFilter.relationshipName !== defaultRelationshipName &&
        ((relatedEntityFilter.relatedEntities ?? []).length > 0 ||
          isValidSelectionCriteria(relatedEntityFilter?.selectionCriteria)),
    );
  }, [relatedEntityFilter]);

  const onSave = useCallback(() => {
    if (relatedEntityFilter) {
      onSaveOrUpdateFilter(relatedEntityFilter);
    }
  }, [onSaveOrUpdateFilter, relatedEntityFilter]);

  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return rawRelatedEntityDataLoading ? (
    <BorderedSection title="Specify Related Entity Filter">
      Loading related entity information...
    </BorderedSection>
  ) : rawRelatedEntityDataError ? (
    <BorderedSection title="Specify Related Entity Filter">
      Error loading related related entity fields data:{' '}
      {rawRelatedEntityDataError.message}
    </BorderedSection>
  ) : relatedEntityFilter ? (
    <BorderedSection title="Specify Related Entity Filter">
      <Box sx={{ minWidth: 120 }}>
        <FormControl fullWidth>
          <InputLabel id="related-entity-select-label">
            Select Related Entity
          </InputLabel>
          <Select
            labelId="related-entity-select-label"
            id="related-entity-select"
            label="Select Related Entity (relationship)"
            value={
              selectedRelatedEntityType?.relationshipName ??
              defaultRelationshipName
            }
            onChange={handleRelatedEntityTypeChange}
            disabled={!isNew}
          >
            {relatedEntityData.map(
              ({ relationshipName, description }: RelationshipInfo) => (
                <MenuItem key={relationshipName} value={relationshipName}>
                  {description}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>

        {relatedEntityFilter.selectionCriteria &&
        selectedRelatedEntityType?.relatedModelName ? (
          <EntitySelectionBuilder
            value={relatedEntityFilter.selectionCriteria}
            relatedEntityFilters={relatedEntityFilter.relatedEntities}
            onUpdateRequest={onUpdateRequest}
            tiers={tiers}
            targetModel={selectedRelatedEntityType?.relatedModelName}
          />
        ) : null}
      </Box>
      <div>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onSave} variant="contained" disabled={!isFilterValid}>
          Save Filter
        </Button>
      </div>
    </BorderedSection>
  ) : (
    <BorderedSection title="Specify Related Entity Filter">
      Specified related entity filter not found.
    </BorderedSection>
  );
};
